/**
 * client/src/components/premium/premium-panel.tsx
 *
 * The signature surface of the Strenn visual language: a deep navy→emerald
 * gradient card with a hairline border and an optional dot-grid texture. This
 * is the logged-in-app counterpart to the setup flow's welcome/mission panels,
 * so onboarding and the authenticated app read as one product.
 *
 * Variants scale the richness (the setup/onboarding flow is the reference — the
 * logged-in app is emerald-forward to match it, owner-approved 2026-07-16):
 *   - "hero"  — emerald-forward gradient + emerald dot-grid + emerald glow.
 *     One per page, top-of-page.
 *   - "panel" — subtle green-navy gradient. The default content card.
 *   - "plain" — token bg-elevated only (a themable, texture-free fallback).
 *
 * All emerald here is delivered via inline gradient / SVG hex — NOT raw
 * `emerald-*` Tailwind classes — so it stays within the design-token gate
 * (scripts/check-design-tokens.mjs) while reading fully green.
 */

import type { CSSProperties, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { DotGrid } from "./dot-grid";

type PanelVariant = "hero" | "panel" | "plain";

/** 145deg emerald→navy — the signature welcome/hero gradient. Exported so the
 *  setup flow's welcome card renders from the same string (Reuse-First). */
export const HERO_GRADIENT = "linear-gradient(145deg, #041B12 0%, #08251A 40%, #0A192F 100%)";
/** 135deg subtle green-navy — the default content-panel fill. */
export const PANEL_GRADIENT = "linear-gradient(135deg, #06140E 0%, #0A192F 100%)";
/** Reduced-strength navy wash for dense data surfaces where the green-tinted
 *  PANEL_GRADIENT would tint content or fight light-mode surfaces (e.g. Activity's table container). */
export const PANEL_WASH_NAVY = "linear-gradient(135deg, rgba(10,25,47,0.5) 0%, rgba(10,25,47,0) 55%)";
/**
 * Ambient brand texture for the logged-in app shell (`app-shell.tsx`) — a
 * faint emerald radial wash anchored above the page, echoing the login/setup
 * flow's hero gradients so the logged-in app and onboarding read as one
 * product (owner-approved emerald-forward shell direction, #1676). Emerald
 * here is 5% alpha — this is decorative texture, NOT a status/success signal;
 * CLAUDE.md's colour semantics (emerald = success/hardware/AAL milestone
 * only) are unaffected. Exported so setup/login can adopt the same string
 * later without duplicating it (Reuse-First Check).
 */
export const SHELL_AMBIENT_WASH =
  "radial-gradient(1200px 480px at 50% -160px, rgba(16,185,129,0.05), transparent 70%)";

/**
 * Modal/sheet backdrop scrim — the dimming layer behind a dialog, drawer, or
 * bottom sheet (NOT the panel content itself, which stays a fully opaque
 * token surface per DESIGN-SYSTEM.md §3.11 "Layered Surface Opacity").
 * Exempt from the full-opacity rule as a decorative dimming layer, but
 * standardised here so every scrim site shares one definition (Reuse-First
 * Check) instead of each hand-typing the same Tailwind literal.
 */
export const SCRIM_CLASS = "bg-black/60 backdrop-blur-sm";

/** Hairline edge ring — the 0.5px translucent-white outline that reads as a
 *  subtle bevel on every premium chrome surface (buttons, toolbars, dropdown
 *  panels, cards). Exported so DESIGN-SYSTEM §3-aligned surfaces share one
 *  definition instead of duplicating the literal per call site. */
export const HAIRLINE_RING = "0 0 0 0.5px rgba(255,255,255,0.06)";
/** Tailwind arbitrary-value form of HAIRLINE_RING, for className-only sites
 *  (no inline `style` prop in play) — kept in sync with the constant above. */
export const HAIRLINE_RING_CLASS = "shadow-[0_0_0_0.5px_rgba(255,255,255,0.06)]";

/** Blue focus-glow ring for form inputs, DESIGN-SYSTEM.md §3.4 — shared by
 *  `ui/input.tsx` and any local input recipe (e.g. AuditFilters) so the two
 *  documented-as-one "shared input recipe" call sites can't drift apart. */
export const FOCUS_GLOW_BLUE_CLASS = "focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)]";

const GRADIENTS: Record<PanelVariant, string | undefined> = {
  hero: HERO_GRADIENT,
  panel: PANEL_GRADIENT,
  plain: undefined,
};

export interface PremiumPanelProps {
  children: ReactNode;
  variant?: PanelVariant;
  /** Show the emerald dot-grid texture. Defaults on for "hero", off otherwise. */
  dotGrid?: boolean;
  /** Stagger index for the entrance animation (0.06s per step). */
  index?: number;
  /** Lift slightly on hover — off for static hero surfaces. */
  hover?: boolean;
  className?: string;
  style?: CSSProperties;
  testId?: string;
}

export function PremiumPanel({
  children,
  variant = "panel",
  dotGrid,
  index = 0,
  hover = false,
  className,
  style,
  testId,
}: PremiumPanelProps) {
  const reduce = useReducedMotion();
  const showDots = dotGrid ?? variant === "hero";
  const background = GRADIENTS[variant];

  return (
    <motion.div
      data-testid={testId}
      initial={reduce ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 400, damping: 28, delay: reduce ? 0 : index * 0.06 }}
      whileHover={hover && !reduce ? { scale: 1.005 } : undefined}
      className={`relative min-w-0 max-w-full overflow-hidden rounded-2xl border border-border-default ${
        variant === "plain" ? "bg-bg-elevated" : ""
      } ${className ?? ""}`}
      style={{ backgroundImage: background, boxShadow: HAIRLINE_RING, ...style }}
    >
      {/* Emerald top-edge sheen — the green hairline that reads across the top of
          every premium surface (owner-approved emerald-forward direction). */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 z-10 h-px"
        style={{ background: "linear-gradient(90deg, transparent 0%, rgba(16,185,129,0.35) 50%, transparent 100%)" }}
      />
      {/* Hero carries the emerald dot-grid; content panels stay texture-free. */}
      {showDots && (variant === "hero" ? <DotGrid color="#10B981" opacity={0.09} /> : <DotGrid />)}
      <div className="relative z-[1]">{children}</div>
    </motion.div>
  );
}
